/*
===============================================================================
bieonboardingworkbook.normalizers.ts
===============================================================================
*/

import {
  BieOnboardingWorkbookOperationResult,
  BieOnboardingWorkbookReview,
  BieOnboardingWorkbookReviewImport,
} from './bieonboardingworkbook.models';


function asString(
  value: any,
  fallback = '',
): string {
  return (
    value === undefined ||
    value === null
  )
    ? fallback
    : String(value);
}


function asNullableString(
  value: any,
): string | null {
  return (
    value === undefined ||
    value === null ||
    value === ''
  )
    ? null
    : String(value);
}


function asNumber(
  value: any,
  fallback = 0,
): number {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : fallback;
}


function asNullableNumber(
  value: any,
): number | null {
  if (
    value === undefined ||
    value === null ||
    value === ''
  ) {
    return null;
  }

  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}


function asBoolean(
  value: any,
): boolean {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true'
  );
}


function asArray(
  value: any,
): any[] {
  return Array.isArray(value)
    ? value
    : [];
}


function asStringArray(
  value: any,
): string[] {
  return asArray(value)
    .map(
      item => String(item),
    );
}


function normalizeImport(
  source: any,
): BieOnboardingWorkbookReviewImport {
  return {
    rid:
      asNumber(source?.rid),

    rix:
      asString(source?.rix),

    trackId:
      asString(source?.trackId),

    loadSessionId:
      asNumber(source?.loadSessionId),

    sourceId:
      asNullableNumber(source?.sourceId),

    sourceCode:
      asString(source?.sourceCode),

    sourceName:
      asString(source?.sourceName),

    classId:
      asNullableNumber(source?.classId),

    classCode:
      asString(source?.classCode),

    className:
      asString(source?.className),

    stateId:
      asNullableNumber(source?.stateId),

    stateCode:
      asString(source?.stateCode),

    stateName:
      asString(source?.stateName),

    name:
      asString(source?.name),

    code:
      asString(source?.code),

    description:
      asNullableString(source?.description),

    filename:
      asString(source?.filename),

    sheetName:
      asString(source?.sheetName),

    workbookVersion:
      asString(source?.workbookVersion),

    checksum:
      asString(source?.checksum),

    totalRows:
      asNumber(source?.totalRows),

    stagedRows:
      asNumber(source?.stagedRows),

    pendingRows:
      asNumber(source?.pendingRows),

    validRows:
      asNumber(source?.validRows),

    warningRows:
      asNumber(source?.warningRows),

    failedRows:
      asNumber(source?.failedRows),

    rejectedRows:
      asNumber(source?.rejectedRows),

    committedRows:
      asNumber(source?.committedRows),

    statusCode:
      asString(source?.statusCode),

    message:
      asString(source?.message),

    uploaderId:
      asNullableNumber(source?.uploaderId),

    uploaderIx:
      asNullableString(source?.uploaderIx),

    uploaderCode:
      asString(source?.uploaderCode),

    uploaderName:
      asString(source?.uploaderName),

    uploaderEmail:
      asString(source?.uploaderEmail),

    uploadedAt:
      asNullableString(source?.uploadedAt),

    created:
      asNullableString(source?.created),

    extraInfo:
      (
        source?.extraInfo &&
        typeof source.extraInfo === 'object'
      )
        ? source.extraInfo
        : {},

    status:
      asNumber(
        source?.status,
        1,
      ),

    stamp:
      asString(source?.stamp),

    raw:
      source,
  };
}


export function normalizeBieOnboardingWorkbookReview(
  source: any,
): BieOnboardingWorkbookReview {
  const summary =
    source?.summary || {};

  const actions =
    source?.actions || {};

  return {
    loadSessionId:
      asNumber(source?.loadSessionId),

    loadSessionIx:
      asString(source?.loadSessionIx),

    loadSessionCode:
      asString(source?.loadSessionCode),

    loadSessionName:
      asString(source?.loadSessionName),

    scopeId:
      asNumber(source?.scopeId),

    scopeCode:
      asString(source?.scopeCode),

    scopeName:
      asString(source?.scopeName),

    stateId:
      asNumber(source?.stateId),

    stateCode:
      asString(source?.stateCode),

    stateName:
      asString(source?.stateName),

    sourceOrganization:
      asString(source?.sourceOrganization),

    sourceSystem:
      asString(source?.sourceSystem),

    coverageStartDate:
      asNullableString(
        source?.coverageStartDate,
      ),

    coverageEndDate:
      asNullableString(
        source?.coverageEndDate,
      ),

    historicalCutoffDate:
      asNullableString(
        source?.historicalCutoffDate,
      ),

    observedCoverageStartDate:
      asNullableString(
        source?.observedCoverageStartDate,
      ),

    observedCoverageEndDate:
      asNullableString(
        source?.observedCoverageEndDate,
      ),

    historicalCoverageStatus:
      asString(source?.historicalCoverageStatus),

    historicalCoverageLabel:
      asString(source?.historicalCoverageLabel),

    stamp:
      asString(source?.stamp),

    workbookAvailable:
      asBoolean(
        source?.workbookAvailable,
      ),

    workbookStatus:
      asString(
        source?.workbookStatus,
        'NOT_UPLOADED',
      ),

    latestImportId:
      asNullableNumber(
        source?.latestImportId,
      ),

    latestImportIx:
      asNullableString(
        source?.latestImportIx,
      ),

    latestImportTrackId:
      asNullableString(
        source?.latestImportTrackId,
      ),

    sourceCode:
      asNullableString(
        source?.sourceCode,
      ),

    filename:
      asNullableString(
        source?.filename,
      ),

    workbookVersion:
      asNullableString(
        source?.workbookVersion,
      ),

    checksum:
      asNullableString(
        source?.checksum,
      ),

    cohortMethod:
      asString(
        source?.cohortMethod,
      ),

    uploaderId:
      asNullableNumber(
        source?.uploaderId,
      ),

    uploaderIx:
      asNullableString(
        source?.uploaderIx,
      ),

    uploaderCode:
      asNullableString(
        source?.uploaderCode,
      ),

    uploaderName:
      asNullableString(
        source?.uploaderName,
      ),

    uploaderEmail:
      asNullableString(
        source?.uploaderEmail,
      ),

    uploadedAt:
      asNullableString(
        source?.uploadedAt,
      ),

    workbookCreatedAt:
      asNullableString(
        source?.workbookCreatedAt,
      ),

    summary: {
      importCount:
        asNumber(summary?.importCount),

      worksheetCount:
        asNumber(summary?.worksheetCount),

      totalRows:
        asNumber(summary?.totalRows),

      stagedRows:
        asNumber(summary?.stagedRows),

      pendingRows:
        asNumber(summary?.pendingRows),

      validRows:
        asNumber(summary?.validRows),

      warningRows:
        asNumber(summary?.warningRows),

      failedRows:
        asNumber(summary?.failedRows),

      rejectedRows:
        asNumber(summary?.rejectedRows),

      committedRows:
        asNumber(summary?.committedRows),
    },

    imports:
      asArray(source?.imports)
        .map(normalizeImport),

    warnings:
      asStringArray(source?.warnings),

    errors:
      asStringArray(source?.errors),

    validationStatus:
      asString(
        source?.validationStatus,
      ),

    validationReady:
      asBoolean(
        source?.validationReady,
      ),

    blockingIssueCount:
      asNumber(
        source?.blockingIssueCount,
      ),

    unresolvedReferenceCount:
      asNumber(
        source?.unresolvedReferenceCount,
      ),

    actions: {
      uploadRequired:
        asBoolean(
          actions?.uploadRequired,
        ),

      replaceAllowed:
        asBoolean(
          actions?.replaceAllowed,
        ),

      openWorkbench:
        asBoolean(
          actions?.openWorkbench,
        ),

      validate:
        asBoolean(
          actions?.validate,
        ),

      publishReady:
        asBoolean(
          actions?.publishReady,
        ),
    },

    raw:
      source,
  };
}


export function normalizeBieOnboardingWorkbookOperation(
  source: any,
): BieOnboardingWorkbookOperationResult {
  return {
    rid:
      asNullableNumber(
        source?.rid,
      ),

    stamp:
      asNullableString(
        source?.stp ??
        source?.stamp,
      ),

    loadSessionId:
      asNullableNumber(
        source?.loadSessionId ??
        source?.lsi ??
        source?.rid,
      ),

    raw:
      source,
  };
}

